import { AlbertValueObject } from '../member/member.model'
import { PolicyCoverageType } from '@shared/models/policy-coverage.model'

/**
 * One limit of a sub-coverage as returned by the backend, e.g. its limit, aggregate limit or a sublimit.
 */
export interface CoverageLimitApiResponse {
  /** Unique identifier of the limit */
  uuid: string;
  /** Sub-coverage the limit belongs to */
  subCoverageUuid: string;
  /** Kind of limit, e.g. LIMIT, AGGREGATE, CONFISCATION or SPARES_DEDUCTIBLE */
  subcoverageLimitType: string;
  /** Amount, Origami value and override */
  subcoverageLimitValue: AlbertValueObject<number | null>;
  /** Currency of the amount, Origami value and override */
  subcoverageLimitCurrency: AlbertValueObject<string | null>;
}

/**
 * A war excess limit for one aircraft type as returned by the backend.
 */
export interface AircraftLimitApiResponse {
  /** Unique identifier of the entry; `null` for Origami entries and new ones */
  uuid: string | null;
  /** Aircraft type the limit applies to */
  aircraftType: string | null;
  /** Limit amount */
  limitValue: number | null;
  /** Currency of the limit amount */
  limitCurrency: string | null;
}

/** Values of `AircraftLimitConsistencyApiResponse`. */
export const AIRCRAFT_LIMIT_CONSISTENCY_API_RESPONSE = {
  CONSISTENT: 'CONSISTENT',
  VARIOUS: 'VARIOUS'
} as const

/**
 * Whether all war excess limits per aircraft type share one limit (`CONSISTENT`) or differ (`VARIOUS`).
 */
export type AircraftLimitConsistencyApiResponse = typeof AIRCRAFT_LIMIT_CONSISTENCY_API_RESPONSE[keyof typeof AIRCRAFT_LIMIT_CONSISTENCY_API_RESPONSE]

/**
 * A sub-coverage as returned by and sent to the backend.
 */
export interface SubCoverageApiResponse {
  /** Unique identifier of the sub-coverage */
  uuid: string;
  /** Coverage the sub-coverage belongs to */
  coverageUuid: string;
  /** Origami identifier; `null` when the sub-coverage only exists in Albert */
  externalId: string | null;
  /** Kind of sub-coverage, e.g. HULL_ALL_RISK */
  subCoverageType: string;
  /** Policy number, Origami value and override */
  number: AlbertValueObject<string>;
  /** Start of the sub-coverage period, Origami value and override */
  effectiveDate: AlbertValueObject<string>;
  /** End of the sub-coverage period, Origami value and override */
  expiryDate: AlbertValueObject<string>;
  /** Whether the sub-coverage is standalone or a member coverage, Origami value and override */
  policyCoverageType: AlbertValueObject<PolicyCoverageType | null>;
  /** Reinsurance share in percent, Origami value and override */
  reinsuranceShare: AlbertValueObject<number | null>;
  /** Reinsurance fronting company, Origami value and override */
  reinsuranceFrontingCompany: AlbertValueObject<string | null>;
  /** Additional reinsurance order information entered in Albert */
  reinsuranceOrderAdditional: string | null;
  /** Limits and sublimits of the sub-coverage */
  subcoverageLimits: CoverageLimitApiResponse[];
  /** Entered in Albert, only for the Aviation Third Party War Excess Liability. */
  aircraftLimits?: AircraftLimitApiResponse[] | null;
  /** Derived from the fleet list by the backend, read-only. */
  origamiAircraftLimits?: AircraftLimitApiResponse[] | null;
  /**
   * Whether the war excess limits per aircraft type share one limit; derived by the backend from the entries made in
   * Albert, or from the fleet list when there are none. Only set for the Aviation Third Party War Excess Liability.
   */
  aircraftLimitConsistency?: AircraftLimitConsistencyApiResponse | null | undefined;
}

/**
 * A coverage of a policy as returned by and sent to the backend, with its sub-coverages.
 */
export interface CoverageApiResponse {
  /** Unique identifier of the coverage; empty for a coverage not saved yet */
  uuid: string;
  /** Policy the coverage belongs to */
  policyId: string;
  /** Origami identifier; `null` when the coverage only exists in Albert */
  externalId: string | null;
  /** Kind of coverage: AVIATION_HULL, AVIATION_LIABILITIES or COMMERCIAL_GENERAL_LIABILITY */
  coverageType: string;
  /** Sub-coverages of the coverage */
  subcoverages: SubCoverageApiResponse[];
  /** Spares Engine Test Run flag of an Aviation Hull coverage; `null` override means none */
  sparesEngineTestRun?: AlbertValueObject<boolean | null>;
  /** Whether the spares are insured jointly with another insured; Aviation Hull only */
  sparesJointlyFor?: AlbertValueObject<boolean>;
  /** Insured the spares are insured jointly with; Aviation Hull only */
  sparesTwinInsured?: AlbertValueObject<string | null>;
  /** Whether the hull deductible applies jointly with another insured; Aviation Hull only */
  hullDeductibleJointlyFor?: AlbertValueObject<boolean>;
  /** Insured the hull deductible applies jointly with; Aviation Hull only */
  hullDeductibleTwinInsured?: AlbertValueObject<string | null>;
}
